/*
 * The branch table. Row layout, activity dots and the expand behaviour are
 * carried over from the original component; the CI badge and the "open PR"
 * marker are the additions.
 */
import { useState } from 'react'
import { CaretDown, GitBranch, Lock, PullRequestIcon } from './Icons'
import { Avatar, Checks } from './Bits'
import { rel } from '../lib/format'
import type { Branch } from '../types'

function Dots({ branch }: { branch: Branch }) {
  const dots = branch.commits.slice(0, 6).reverse()
  return (
    <div className="activity" aria-hidden="true">
      <div className="line" />
      <div className="dots">
        {dots.map((c, i) => (
          <div key={c.sha + i} className={`dot ${i === dots.length - 1 ? 'latest' : ''}`} />
        ))}
      </div>
    </div>
  )
}

function Row({ branch }: { branch: Branch }) {
  const [open, setOpen] = useState(false)
  const last = branch.commits[0]
  const tooltip = branch.role ? `${branch.name} · ${branch.role}` : branch.name

  return (
    <div className="branch">
      <button
        type="button"
        className="branch-row"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        title={`${tooltip}\n${branch.totalCommits.toLocaleString('en-US')} commits`}
      >
        <div className="branch-name">
          <span className="ic"><GitBranch size={14} /></span>
          <span className="nm">{branch.name}</span>
          {branch.env ? (
            <span className={`tag ${branch.env === 'production' ? 'tag-accent' : 'tag-neutral'}`}>{branch.env}</span>
          ) : null}
          {branch.protected ? <span className="lock" title="Protected branch"><Lock size={12} /></span> : null}
          {branch.pr ? (
            <span className="check none" title={`Open pull request #${branch.pr.number} into ${branch.pr.base}`}>
              <PullRequestIcon size={12} /> #{branch.pr.number}
            </span>
          ) : null}
        </div>
        <Dots branch={branch} />
        <div className="lastcommit">
          <div className="msg" title={last?.message}>{last?.message ?? '-'}</div>
          <div className="rel">
            <span>{rel(last?.committedAt)}</span>
            <Checks state={branch.checks} />
          </div>
        </div>
        <div className="author">
          {last ? <Avatar person={last.author} large /> : null}
          <span className="alias">{last?.author.alias}</span>
        </div>
        <div className={`chev ${open ? 'open' : ''}`}><CaretDown size={14} /></div>
      </button>

      {open ? (
        <div className="commits">
          {branch.commits.map(c => (
            <div className="commit" key={c.sha}>
              <span className="bullet" />
              <span className="sha">{c.sha}</span>
              <span className="msg" title={c.message}>{c.message}</span>
              <Checks state={c.checks} />
              <span className="alias">{c.author.alias}</span>
              <span className="rel">{rel(c.committedAt)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function BranchTable({ branches, total, truncated }: { branches: Branch[]; total: number; truncated: boolean }) {
  return (
    <section className="section">
      <div className="section-label">
        Branches <span className="count">· {branches.length}{branches.length !== total ? ` of ${total}` : ''}</span>
      </div>
      <div className="grid-head">
        <div>Branch</div>
        <div>Activity</div>
        <div>Last commit</div>
        <div>Author</div>
        <div />
      </div>
      {branches.length === 0 ? (
        <div className="empty">
          {total === 0 ? 'This repository has no branches yet. Nothing has been pushed to it.' : 'No branches match this filter.'}
        </div>
      ) : (
        branches.map(b => <Row key={b.name} branch={b} />)
      )}
      {truncated ? (
        <div className="note">Showing the 100 most recently updated branches of {total.toLocaleString('en-US')}.</div>
      ) : null}
    </section>
  )
}
